import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const rupiah = (value) => `Rp ${(Number(value) || 0).toLocaleString("id-ID")}`;

export default function StockOutSale() {
  const [options, setOptions] = useState({ locations: [], ingredients: [] });
  const [products, setProducts] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [locationType, setLocationType] = useState("warehouse");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [transactionDate, setTransactionDate] = useState(() => new Intl.DateTimeFormat("en-CA", {timeZone:"Asia/Jakarta", year:"numeric", month:"2-digit", day:"2-digit"}).format(new Date()));
  const [customer, setCustomer] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/stock-out/options"), api.get("/products")])
      .then(([a, b]) => {
        setOptions(a.data || { locations: [], ingredients: [] });
        setProducts(Array.isArray(b.data) ? b.data : b.data?.products || []);
      })
      .catch((e) => setMessage(
        e.response?.data?.detail || "Gagal memuat data gudang dan barang."
      ));
  }, []);

  const locations = options.locations || [];
  const selectedLocation = locations.find((x) =>
    x.id === warehouseId &&
    String(x.type || x.location_type || "").toLowerCase() === locationType
  );

  const availableProducts = products.filter((p) => p.active !== false);
  const availableIngredients = (options.ingredients || []).filter(
    (x) => x.active !== false
  );

  function masterFor(item) {
    const list = item.kind === "ingredient" ? availableIngredients : availableProducts;
    return list.find((x) => x.id === item.item_id);
  }

  function masterPrice(item) {
    const master = masterFor(item);
    if (!master) return 0;
    return Number(item.kind === "ingredient"
      ? (master.cost ?? master.updated_cost ?? master.base_cost ?? 0)
      : (master.price ?? master.hpp ?? 0)) || 0;
  }

  const total = useMemo(() => items.reduce(
    (sum, item) => sum + Math.max(0, Number(item.sale_price) || 0) *
      Math.max(0, Number(item.qty) || 0), 0
  ), [items]);

  function addItem(kind) {
    setItems((prev) => [...prev, {
      kind, item_id: "", qty: "1", sale_price: ""
    }]);
  }

  function updateItem(index, field, value) {
    setItems((prev) => prev.map((item, i) => {
      if (i !== index) return item;
      const next = { ...item, [field]: value };
      if (field === "item_id") {
        next.sale_price = "";
      }
      return next;
    }));
  }

  async function saveSale() {
    setMessage("");
    if (!selectedLocation) {
      setMessage("Pilih lokasi stok (gudang atau outlet).");
      return;
    }
    if (!items.length) {
      setMessage("Tambahkan minimal satu produk atau bahan.");
      return;
    }

    const normalized = [];
    for (const item of items) {
      const master = masterFor(item);
      const qty = Number(item.qty);
      const salePrice = Number(item.sale_price);
      if (!master) {
        setMessage("Pilih barang yang valid pada setiap baris.");
        return;
      }
      if (!Number.isFinite(qty) || qty <= 0) {
        setMessage("Kuantitas harus lebih besar dari nol.");
        return;
      }
      if (!Number.isFinite(salePrice) || salePrice < 0 || item.sale_price === "") {
        setMessage("Isi harga jual aktual yang disepakati.");
        return;
      }
      normalized.push({
        kind: item.kind,
        item_id: master.id,
        qty,
        sale_price: salePrice,
      });
    }

    const key = `stockout-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setSaving(true);
    try {
      const response = await api.post("/stock-out/sales", {
        warehouse_id: locationType === "warehouse" ? warehouseId : null,
        location_type: locationType,
        location_id: warehouseId,
        payment_method: paymentMethod,
        transaction_date: transactionDate,
        items: normalized,
        customer_name: customer.trim() || null,
        notes: notes.trim(),
        idempotency_key: key,
      }, { headers: { "Idempotency-Key": key } });

      const sale = response.data || {};
      setItems([]);
      setCustomer("");
      setNotes("");
      setMessage(
        `Penjualan tersimpan: ${sale.trx_no || sale.id || "OK"} — ` +
        `${rupiah(sale.total)}. Periksa riwayat dan jurnal sebelum operasional.`
      );
    } catch (e) {
      setMessage(e.response?.data?.detail || "Penjualan gagal disimpan.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 rounded-lg border bg-white p-5">
      <h2 className="text-lg font-semibold">Penjualan Langsung Gudang</h2>
      <p className="text-sm text-slate-600">
        Untuk penjualan di luar POS outlet. Harga master menjadi acuan;
        harga jual aktual yang diisi akan digunakan sebagai nilai transaksi.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="space-y-1 text-sm">Tanggal transaksi
          <input type="date" className="w-full rounded-md border p-2"
            value={transactionDate} onChange={(e) => setTransactionDate(e.target.value)} required />
        </label>
        <label className="space-y-1 text-sm">Jenis lokasi stok
          <select className="w-full rounded-md border p-2" value={locationType}
            onChange={(e) => { setLocationType(e.target.value); setWarehouseId(""); setItems([]); }}>
            <option value="warehouse">Gudang</option>
            <option value="outlet">Outlet</option>
          </select>
        </label>
        <label className="space-y-1 text-sm">Lokasi stok berada
          <select className="w-full rounded-md border p-2" value={warehouseId}
            onChange={(e) => { setWarehouseId(e.target.value); setItems([]); }}>
            <option value="">Pilih lokasi</option>
            {locations.filter((x) =>
              String(x.type || x.location_type || "").toLowerCase() === locationType
            ).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-sm">Nama petugas
          <input className="w-full rounded-md border bg-slate-50 p-2"
            value={(() => { try {
              const u = JSON.parse(localStorage.getItem("user") || "{}");
              return u.name || u.full_name || "";
            } catch { return ""; } })()} readOnly />
        </label>
        <label className="space-y-1 text-sm">Metode pembayaran
          <select className="w-full rounded-md border p-2" value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}>
            <option value="cash">Tunai</option><option value="qris">QRIS</option>
            <option value="debit">Debit</option><option value="ewallet">E-wallet</option>
          </select>
        </label>
        <label className="space-y-1 text-sm">Nama pelanggan (opsional)
          <input className="w-full rounded-md border p-2" value={customer}
            onChange={(e) => setCustomer(e.target.value)} />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="rounded-md border px-3 py-2 text-sm"
          disabled={!selectedLocation || saving} onClick={() => addItem("product")}>
          + Tambah Produk
        </button>
        <button type="button" className="rounded-md border px-3 py-2 text-sm"
          disabled={!selectedLocation || saving} onClick={() => addItem("ingredient")}>
          + Tambah Bahan
        </button>
      </div>

      {items.map((item, index) => {
        const master = masterFor(item);
        const list = item.kind === "ingredient" ? availableIngredients : availableProducts;
        const price = masterPrice(item);
        const qty = Number(item.qty) || 0;
        const lineTotal = (Number(item.sale_price) || 0) * qty;
        return (
          <div key={`${item.kind}-${index}`} className="space-y-3 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <strong className="text-sm">
                {item.kind === "ingredient" ? "Bahan baku" : "Produk jadi"}
              </strong>
              <button type="button" disabled={saving} className="text-sm text-red-600"
                onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}>
                Hapus
              </button>
            </div>
            <label className="block space-y-1 text-sm">Pilih barang
              <select className="w-full rounded-md border p-2" value={item.item_id}
                onChange={(e) => updateItem(index, "item_id", e.target.value)}>
                <option value="">Pilih {item.kind === "ingredient" ? "bahan" : "produk"}</option>
                {list.map((x) =>
                  <option key={x.id} value={x.id}>
                    {x.name} — acuan {rupiah(
                      item.kind === "ingredient"
                        ? (x.cost ?? x.updated_cost ?? x.base_cost ?? 0)
                        : (x.price ?? x.hpp ?? 0)
                    )}
                  </option>
                )}
              </select>
            </label>
            {master && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="text-sm">
                  <span className="text-slate-500">Harga master/acuan</span>
                  <p className="mt-1 font-medium">{rupiah(price)}</p>
                </div>
                <label className="space-y-1 text-sm">Harga jual aktual
                  <input className="w-full rounded-md border p-2" type="number"
                    min="0" step="any" value={item.sale_price}
                    onChange={(e) => updateItem(index, "sale_price", e.target.value)}
                    placeholder="Masukkan harga jual" />
                </label>
                <label className="space-y-1 text-sm">
                  Kuantitas {item.kind === "ingredient" ? `(${master.unit || "satuan"})` : "(pcs)"}
                  <input className="w-full rounded-md border p-2" type="number"
                    min="0.001" step={item.kind === "ingredient" ? "any" : "1"}
                    value={item.qty}
                    onChange={(e) => updateItem(index, "qty", e.target.value)} />
                </label>
              </div>
            )}
            <div className="text-right text-sm font-medium">
              Subtotal: {rupiah(lineTotal)}
            </div>
          </div>
        );
      })}

      <label className="block space-y-1 text-sm">Catatan
        <textarea className="w-full rounded-md border p-2" rows={2}
          value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      <div className="border-t pt-3 text-right font-semibold">
        Total penjualan: {rupiah(total)}
      </div>

      {message && <p role="status" className="text-sm text-slate-700">{message}</p>}

      <button type="button" disabled={saving || !selectedLocation || !items.length}
        onClick={saveSale}
        className="rounded-md bg-blue-600 px-4 py-2 text-white disabled:cursor-not-allowed disabled:bg-gray-400">
        {saving ? "Menyimpan..." : "Simpan Penjualan Gudang"}
      </button>
    </div>
  );
}
